import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import {
  isSignatureCheckingActive,
  SIGNATURE_CHECK_DEADLINE_MS,
  type HealedSignature,
  type SignatureRefreshHooks,
} from '../../services/messageDecryptionPipeline';
import {
  applySignatureRowPatches,
  applySignatureRowPatchesToRecord,
  buildCheckingPatches,
  buildRecheckPatches,
  collectPendingSignatureRows,
  EMPTY_SIGNATURE_RECHECK_SUMMARY,
  type SignatureRecheckSummary,
  type SignatureRowPatch,
} from '../../services/signatureRecheck';
import { loadPinnedMemberIdentityIds } from '../../services/wrappedKeyMembership';
import type { ConversationMessagesState, DisplayMessage, SignatureRefreshNotice } from './types';

export interface UseSignatureRefreshParams {
  identityId: string | null;
  activeConversationId: string | null;
  activeConversationIdRef: MutableRefObject<string | null>;
  messagesStateRef: MutableRefObject<Record<string, ConversationMessagesState>>;
  replyParentHydrationRef: MutableRefObject<Record<string, Record<string, DisplayMessage>>>;
  setMessagesState: Dispatch<SetStateAction<Record<string, ConversationMessagesState>>>;
  setReplyParentHydration: Dispatch<SetStateAction<Record<string, Record<string, DisplayMessage>>>>;
  signingKeyCache: MutableRefObject<Record<string, string>>;
  pqSigningKeyCache: MutableRefObject<Record<string, string>>;
  refetchPqSigningKey: (identityId: string, options?: { force?: boolean }) => Promise<void>;
  canRefetchPqSigningKey: (identityId: string, options?: { force?: boolean }) => boolean;
}

/**
 * Heals rows whose hybrid signature failed only because the sender's ML-DSA
 * key was not cached yet, and tracks the "keys refreshed" notice shown when
 * rows that were already rendered as unverified become verified.
 */
export function useSignatureRefresh(params: UseSignatureRefreshParams) {
  const {
    identityId,
    activeConversationId,
    activeConversationIdRef,
    messagesStateRef,
    replyParentHydrationRef,
    setMessagesState,
    setReplyParentHydration,
    signingKeyCache,
    pqSigningKeyCache,
    refetchPqSigningKey,
    canRefetchPqSigningKey,
  } = params;

  const [signatureRefreshNotice, setSignatureRefreshNotice] = useState<SignatureRefreshNotice | null>(null);

  useEffect(() => {
    setSignatureRefreshNotice(null);
  }, [activeConversationId]);

  const dismissSignatureRefreshNotice = useCallback(() => {
    setSignatureRefreshNotice(null);
  }, []);

  const reportHealed = useCallback(
    (conversationId: string, healed: HealedSignature[]) => {
      if (conversationId !== activeConversationIdRef.current) return;
      const visible = healed.filter((h) => h.wasVisiblyUnverified);
      if (visible.length === 0) return;
      setSignatureRefreshNotice((prev) => {
        const base = prev?.conversationId === conversationId ? prev : null;
        const ids = new Set(base?.senderIdentityIds ?? []);
        for (const h of visible) ids.add(h.message.fromIdentityId);
        return {
          conversationId,
          healedCount: (base?.healedCount ?? 0) + visible.length,
          senderIdentityIds: [...ids],
        };
      });
    },
    [activeConversationIdRef]
  );

  const applyPatches = useCallback(
    (conversationId: string, patches: Map<string, SignatureRowPatch>) => {
      if (patches.size === 0) return;
      setMessagesState((prev) => {
        const st = prev[conversationId];
        if (!st) return prev;
        const messages = applySignatureRowPatches(st.messages, patches);
        if (messages === st.messages) return prev;
        return { ...prev, [conversationId]: { ...st, messages: messages as DisplayMessage[] } };
      });
      setReplyParentHydration((prev) => {
        const conv = prev[conversationId];
        if (!conv) return prev;
        const next = applySignatureRowPatchesToRecord(conv, patches);
        if (next === conv) return prev;
        return { ...prev, [conversationId]: next as Record<string, DisplayMessage> };
      });
    },
    [setMessagesState, setReplyParentHydration]
  );

  const snapshotPending = useCallback(
    (conversationId: string): DisplayMessage[] => {
      const byId = new Map<string, DisplayMessage>();
      for (const m of messagesStateRef.current[conversationId]?.messages ?? []) byId.set(m.id, m);
      for (const m of Object.values(replyParentHydrationRef.current[conversationId] ?? {})) {
        if (!byId.has(m.id)) byId.set(m.id, m);
      }
      return collectPendingSignatureRows(byId.values());
    },
    [messagesStateRef, replyParentHydrationRef]
  );

  const recheckPendingSignatures = useCallback(
    async (conversationId: string, options?: { force?: boolean }): Promise<SignatureRecheckSummary> => {
      if (!identityId) return EMPTY_SIGNATURE_RECHECK_SUMMARY;
      const pending = snapshotPending(conversationId);
      if (pending.length === 0) return EMPTY_SIGNATURE_RECHECK_SUMMARY;

      const now = Date.now();
      const visibleBefore = new Set(
        pending.filter((m) => !isSignatureCheckingActive(m, now)).map((m) => m.id)
      );
      const senders = new Set(pending.map((m) => m.fromIdentityId));
      const fetching = new Set(
        [...senders].filter(
          (sid) => !pqSigningKeyCache.current[sid] && canRefetchPqSigningKey(sid, options)
        )
      );
      if (fetching.size > 0) {
        applyPatches(
          conversationId,
          buildCheckingPatches(pending, fetching, now + SIGNATURE_CHECK_DEADLINE_MS)
        );
        await Promise.all(
          [...fetching].map((sid) => refetchPqSigningKey(sid, options).catch(() => undefined))
        );
      }

      let pinnedIdentityIds: string[] | null = null;
      try {
        pinnedIdentityIds = await loadPinnedMemberIdentityIds(conversationId);
      } catch {
        pinnedIdentityIds = null;
      }

      const { patches, healed, summary } = buildRecheckPatches({
        rows: snapshotPending(conversationId),
        signingKeys: signingKeyCache.current,
        pqSigningKeys: pqSigningKeyCache.current,
        pinnedIdentityIds,
        identityId,
      });
      applyPatches(conversationId, patches);
      if (healed.length > 0) {
        reportHealed(
          conversationId,
          healed.map((h) =>
            visibleBefore.has(h.message.id) ? { ...h, wasVisiblyUnverified: true } : h
          )
        );
      }
      return summary;
    },
    [
      identityId,
      snapshotPending,
      pqSigningKeyCache,
      signingKeyCache,
      canRefetchPqSigningKey,
      refetchPqSigningKey,
      applyPatches,
      reportHealed,
    ]
  );

  const recheckRef = useRef(recheckPendingSignatures);
  recheckRef.current = recheckPendingSignatures;

  const signatureHooks: SignatureRefreshHooks = useMemo(
    () => ({
      refetchPqSigningKey: (id: string) => refetchPqSigningKey(id),
      onPqRefetchDeferred: (conversationId, _senderIds, settled) => {
        void settled.finally(() => {
          void recheckRef.current(conversationId).catch(() => undefined);
        });
      },
      onSignaturesHealed: reportHealed,
    }),
    [refetchPqSigningKey, reportHealed]
  );
  const signatureHooksRef = useRef<SignatureRefreshHooks | undefined>(signatureHooks);
  signatureHooksRef.current = signatureHooks;

  return {
    signatureHooksRef,
    recheckPendingSignatures,
    signatureRefreshNotice,
    dismissSignatureRefreshNotice,
  };
}
